import React, { useMemo } from 'react';
import { Typography, Stack, Tooltip, Box, Divider } from '@mui/material';
import { PlatformCardContainer, PlatformImg } from '../styles';
import { FALLBACK_INSPECTION_IMAGE } from '@/constants';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { abyssColors } from '@/theme/colors';
import { Maybe, PlatformCardInsights } from '@/__generated__/graphql';
import { useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/state/atoms';
import {
  consolidateInsightsByCategory,
  InsightWithFilterOptions,
  RiskCalculationConfig,
} from '@/utils/platformRiskCalculations';

type PlatformDisplay = 'coating' | 'health' | 'criticality';

type PlatformCardType = {
  name: string;
  imageUrl?: string;
  subHeading: string;
  children?: React.ReactNode;
  platformCardInsights: Maybe<Array<PlatformCardInsights>> | undefined;
  maxValue?: number;
  platformSelectedFilter?: string;
  selectedDeploymentTypes?: string[];
  categoryDataTypes?: Record<string, string[]>;
};

type CardDataProps = {
  name: string;
  value: string;
  percentage?: number;
  color: string;
  divider?: boolean;
  isCoatingCondition?: boolean;
};

const CardData = ({ color, name: scanName, value, percentage, divider }: CardDataProps) => {
  const displayValue = percentage !== undefined ? `${percentage.toFixed(1)}%` : value;

  return (
    <>
      {divider && <Divider sx={{ width: '100%', marginTop: 2 }} />}
      <Stack direction="row" alignItems="center" mt={2} justifyContent="space-between" width="100%">
        <span
          style={{
            backgroundColor: color,
            borderRadius: 2,
            width: 10,
            height: 10,
          }}
        />

        <Typography fontSize={12} color={abyssColors.primary[400]} pl={0.5} noWrap>
          {scanName}
        </Typography>

        <Box
          sx={{
            border: `1px solid ${abyssColors.primary[200]}`,
            mx: 1.5,
            flexGrow: 1,
          }}
        />

        <Typography fontSize={12} color={abyssColors.primary[400]} textAlign="right">
          {displayValue}
        </Typography>
      </Stack>
    </>
  );
};

export const PlatformCard = ({
  name,
  imageUrl,
  subHeading,
  children,
  platformCardInsights,
  maxValue,
  platformSelectedFilter,
  selectedDeploymentTypes = [],
  categoryDataTypes = {},
}: PlatformCardType) => {
  const setPlatformsFilters = useSetAtom(state.platformsFilters);
  const currentPlatformFilter = useAtomValue(state.platformSelectedFilter);

  const cardInsightsMapped = useMemo(() => {
    if (platformCardInsights && platformCardInsights.length > 0) {
      const mappedData = platformCardInsights?.reduce(
        (accumulator, current) => {
          // Keep group names as-is, use 'coating-health' as the key (not 'coating condition')
          const groupKey = current.group.toLowerCase();

          if (!accumulator[groupKey]) {
            accumulator[groupKey] = [];
          }
          accumulator[groupKey].push(current);

          return accumulator;
        },
        {} as Record<string, PlatformCardInsights[]>
      );

      // Apply consolidation logic only to "risk score" group
      Object.keys(mappedData).forEach((groupKey) => {
        const groupInsights = mappedData[groupKey];

        if (groupKey === 'risk score') {
          // Convert to InsightWithFilterOptions format
          const insightsWithFilterOptions: InsightWithFilterOptions[] = groupInsights.map(
            (insight) => ({
              ...insight,
              filterOptions:
                (insight as PlatformCardInsights & { filterOptions?: string[] }).filterOptions ||
                [],
            })
          );

          // Use shared utility function to consolidate insights
          const config: RiskCalculationConfig = {
            selectedDeploymentTypes,
            categoryDataTypes,
          };

          const consolidatedInsights = consolidateInsightsByCategory(
            insightsWithFilterOptions,
            config
          );

          // Update the mapped data with consolidated insights
          mappedData[groupKey] = consolidatedInsights;
        }
        // For other groups (like criticality), keep original insights unchanged
      });

      setPlatformsFilters((previousFilters) => {
        const newFilters = Object.keys(mappedData);

        const allFilters = new Set([...previousFilters, ...newFilters]);
        return [...allFilters] as PlatformDisplay[];
      });

      return mappedData;
    }
    return {};
  }, [platformCardInsights, setPlatformsFilters, selectedDeploymentTypes, categoryDataTypes]);

  // Map filter to group key - 'coating condition' (display label) maps to 'coating-health' (actual key)
  const getFilterKey = (filter: string): string => {
    const normalized = filter.toLowerCase();
    // Map 'coating condition' display label to 'coating-health' key
    if (normalized === 'coating condition') {
      return 'coating-health';
    }
    return normalized;
  };

  const filterKey = getFilterKey(currentPlatformFilter || platformSelectedFilter || '');
  const cardInsights = cardInsightsMapped[filterKey];

  const ColorBar = useMemo(() => {
    if (cardInsights && cardInsights?.length > 0) {
      const total = cardInsights?.reduce((accumulator, item) => {
        return accumulator + Number(item.value);
      }, 0);

      // For coating filter, always use total instead of maxValue
      const isCoatingFilter =
        platformSelectedFilter === 'coating-health' ||
        String(currentPlatformFilter) === 'coating-health' ||
        platformSelectedFilter === 'coating condition' ||
        String(currentPlatformFilter) === 'coating condition';
      const referenceValue = isCoatingFilter ? total : maxValue || total;

      const sections = cardInsights
        .toSorted((a, b) => (a?.displayConfig?.barIndex || 0) - (b?.displayConfig?.barIndex || 0))
        .map((item) => ({
          percentage: (Number(item.value) / referenceValue) * 100,
          color: item?.displayConfig?.color,
          name: item.name,
        }));
      return (
        <Box display="flex" height="24px" width="100%">
          {sections.map((section, index) => (
            <Box
              key={`${section.name}-${index}`}
              borderRadius="1px"
              marginRight={index < sections.length - 1 ? '4px' : 0}
              width={`${section.percentage}%`}
              bgcolor={section.color ?? '#fff'}
            />
          ))}
        </Box>
      );
    }
  }, [cardInsights, maxValue, platformSelectedFilter, currentPlatformFilter]);

  return (
    <PlatformCardContainer>
      <PlatformImg>
        <img
          style={{
            width: '100%',
            aspectRatio: '79/37',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
          src={(imageUrl && getCloudfrontUrl(imageUrl)) || FALLBACK_INSPECTION_IMAGE}
          alt={name}
          onError={(event) => {
            const reference = event.currentTarget;
            reference.src = FALLBACK_INSPECTION_IMAGE;
          }}
          crossOrigin="use-credentials"
        />
      </PlatformImg>
      <Stack>
        <Stack alignItems="flex-start">
          <Tooltip title={<Typography fontSize="1.4rem">{name}</Typography>}>
            <Typography
              fontSize="2rem"
              color="text.primary"
              fontWeight={500}
              lineHeight={1.3}
              marginBottom={0.5}
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                textAlign: 'left',
              }}
            >
              {name}
            </Typography>
          </Tooltip>
          <Typography
            color="text.primary"
            mt={1.2}
            textAlign="left"
            fontSize={'13px'}
            fontWeight={400}
          >
            {subHeading}
          </Typography>
          {cardInsightsMapped && (
            <>
              {cardInsights?.length > 0 &&
                (() => {
                  const isCoatingFilter =
                    platformSelectedFilter === 'coating-health' ||
                    platformSelectedFilter === 'coating condition' ||
                    String(currentPlatformFilter) === 'coating-health' ||
                    String(currentPlatformFilter) === 'coating condition';

                  // Calculate total for percentage calculation
                  const total = cardInsights.reduce((sum, item) => sum + Number(item.value), 0);

                  return (
                    <>
                      {ColorBar}
                      {cardInsights
                        ?.toSorted(
                          (a, b) =>
                            (a?.displayConfig?.listIndex || 0) - (b?.displayConfig?.listIndex || 0)
                        )
                        ?.map((item) => {
                          const numericValue = Number(item.value);
                          const percentage = total > 0 ? (numericValue / total) * 100 : 0;

                          // Format RI name to match chart legends (Ri0, Ri1, etc.)
                          const formatRIName = (name: string): string => {
                            const lowerName = name.toLowerCase();
                            if (lowerName.startsWith('ri')) {
                              return `Ri${lowerName.slice(2)}`;
                            }
                            return name;
                          };

                          const displayName = isCoatingFilter ? formatRIName(item.name) : item.name;

                          return (
                            <CardData
                              key={item.name}
                              name={displayName}
                              color={item?.displayConfig?.color || '#fff'}
                              value=""
                              percentage={isCoatingFilter ? percentage : undefined}
                              divider={Boolean(item?.displayConfig?.divider)}
                            />
                          );
                        })}
                    </>
                  );
                })()}
            </>
          )}
          {children || <></>}
        </Stack>
      </Stack>
    </PlatformCardContainer>
  );
};
